import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout";
import EditProjectModal from "../components/grants/projects/EditProjectModal";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import { formatDate, formatDateTime } from "../utils/date-formatters";
import { isSafeUrl } from "../utils/url-validation";

interface ProjectDetails {
  id: string;
  name: string;
  projectUrl: string | null;
  regions: string[];
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  competition: {
    id: string;
    name: string;
    code: string | null;
    actionCode: string | null;
    program: {
      id: string;
      code: string;
      name: string;
    } | null;
  } | null;
  operator: {
    id: string;
    name: string;
    shortName: string | null;
  } | null;
  callsCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Page component for displaying detailed information about a single operator project.
 */
const GrantsProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, hasModuleEnabled } = useHotel();
  const canWrite = user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: project, isLoading, error, refetch } = useQuery<ProjectDetails>({
    queryKey: ["grants-project", id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/grants/projects/${id}`);
      return data;
    },
    enabled: !!id,
  });

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="animate-pulse">
            <div className="h-4 bg-neutral-200 rounded w-48 mb-4"></div>
            <div className="h-8 bg-neutral-200 rounded w-96 mb-2"></div>
            <div className="h-4 bg-neutral-200 rounded w-64"></div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-neutral-200 rounded w-32"></div>
              <div className="h-4 bg-neutral-200 rounded w-full"></div>
              <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
              <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !project) {
    return (
      <Layout>
        <div className="space-y-6">
          <nav className="text-sm text-neutral-500">
            <Link to="/grants/projects" className="hover:text-primary-600">
              Projekty operatorów
            </Link>
            <span className="mx-2">&gt;</span>
            <span>Błąd</span>
          </nav>

          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center">
            <div className="text-red-600 mb-4">
              <svg
                className="h-12 w-12 mx-auto mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="font-medium">
                Nie udało się pobrać danych projektu
              </p>
              <p className="text-sm mt-1">
                {error instanceof Error
                  ? error.message
                  : "Projekt nie został znaleziony"}
              </p>
            </div>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => refetch()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Spróbuj ponownie
              </button>
              <Link
                to="/grants/projects"
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
              >
                Wróć do listy
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumbs */}
        <nav className="text-sm text-neutral-500" aria-label="Nawigacja breadcrumb">
          <Link to="/grants/projects" className="hover:text-primary-600">
            Projekty operatorów
          </Link>
          <span className="mx-2">&gt;</span>
          <span className="text-neutral-900">{project.name}</span>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              {project.competition?.program && (
                <Link
                  to={`/grants/programs/${project.competition.program.id}`}
                  className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 hover:bg-primary-200"
                >
                  {project.competition.program.code}
                </Link>
              )}
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  project.isActive
                    ? "bg-green-100 text-green-800"
                    : "bg-neutral-200 text-neutral-800"
                }`}
              >
                {project.isActive ? "Aktywny" : "Nieaktywny"}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left mt-2">{project.name}</h1>
          </div>
          {canWrite && (
            <button
              onClick={handleEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Edytuj
            </button>
          )}
        </div>

        {/* Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="text-lg font-medium text-neutral-900 text-left">
              Szczegóły projektu
            </h2>
          </div>
          <div className="px-6 py-4">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Konkurs</dt>
                <dd className="mt-1 text-sm text-left">
                  {project.competition ? (
                    <Link
                      to={`/grants/competitions/${project.competition.id}`}
                      className="text-primary-600 hover:text-primary-800"
                    >
                      {project.competition.name}
                    </Link>
                  ) : (
                    <span className="text-neutral-500">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Operator</dt>
                <dd className="mt-1 text-sm text-left">
                  {project.operator ? (
                    <Link
                      to={`/grants/operators/${project.operator.id}`}
                      className="text-primary-600 hover:text-primary-800"
                    >
                      {project.operator.shortName || project.operator.name}
                    </Link>
                  ) : (
                    <span className="text-neutral-500">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Regiony</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {project.regions && project.regions.length > 0
                    ? project.regions.join(", ")
                    : "-"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Strona projektu</dt>
                <dd className="mt-1 text-sm text-left">
                  {project.projectUrl && isSafeUrl(project.projectUrl) ? (
                    <a
                      href={project.projectUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800"
                    >
                      Otwórz stronę
                    </a>
                  ) : (
                    <span className="text-neutral-500">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Data rozpoczęcia</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {formatDate(project.startDate)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Data zakończenia</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {formatDate(project.endDate)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Liczba naborów</dt>
                <dd className="mt-1 text-sm text-left">
                  {project.callsCount > 0 ? (
                    <Link
                      to={`/grants/calls?projectId=${project.id}`}
                      className="text-primary-600 hover:text-primary-800"
                    >
                      {project.callsCount}
                    </Link>
                  ) : (
                    <span className="text-neutral-500">0</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Utworzono</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {formatDateTime(project.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Zaktualizowano</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {formatDateTime(project.updatedAt)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <EditProjectModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        projectId={id}
      />
    </Layout>
  );
};

export default GrantsProjectDetails;
